// src/components/Admin/SellerManagement/SellerFinancials.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../../../services/apiServices';
import { format } from 'date-fns';

// Import UI Components
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { AlertDialog, 
    AlertDialogAction, 
    AlertDialogCancel, 
    AlertDialogContent, 
    AlertDialogDescription, 
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../../../components/ui/alert-dialog";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../../components/ui/tooltip";
import { ExternalLink, RefreshCw, FileText, Receipt, FilePlus2, FileKey2 } from 'lucide-react';
import RefundInvoiceModal from './RefundInvoiceModal'; // Assumes it's a sibling component
import { useToast } from "../../../../hooks/use-toast"; // --- NEW: Import toast for notifications ---
import { reissueInvoiceAsGst } from '../../../../services/AdminApis'; 
import { usePermissions } from '../../../../hooks/usePermissions';

// Helper Functions
const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);

const getStatusBadge = (item) => {
    if (item.type === 'Credit Note') {
        return <Badge variant="outline">CREDITED</Badge>;
    }
    if (item.is_refunded && item.status !== 'REFUNDED') {
        return <Badge variant="warning">PARTIAL REFUND</Badge>;
    }
    if (item.status === 'CANCELLED') {
        return <Badge variant="warning">CANCELLED</Badge>;
    }
    // --- NEW: Add a badge for reissued invoices ---
    if (item.status === 'REISSUED') {
        return <Badge variant="secondary">REISSUED</Badge>;
    }
    switch (item.status) {
        case 'PAID': return <Badge variant="success">PAID</Badge>;
        case 'REFUNDED': return <Badge variant="destructive">REFUNDED</Badge>;
        default: return <Badge variant="secondary">{item.status}</Badge>;
    }
};

const SellerFinancials = ({ sellerProfileId, logAction }) => {
    const [financials, setFinancials] = useState({ invoices: [], credit_notes: [] });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
        // --- NEW: State for reissue confirmation modal ---
    const [isReissueModalOpen, setIsReissueModalOpen] = useState(false);
    const [invoiceToReissue, setInvoiceToReissue] = useState(null);
    const [isReissuing, setIsReissuing] = useState(false);
    const { toast } = useToast();
    const { hasPermission } = usePermissions();

    const fetchFinancialData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await api.get(`/admin/financials/seller/${sellerProfileId}`, {
                params: { _cacheBust: new Date().getTime() }
            });
            setFinancials(response.data);
        } catch (err) {
            setError('Failed to fetch seller financial data.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [sellerProfileId]);

    useEffect(() => {
        fetchFinancialData();
    }, [fetchFinancialData]);

    const combinedData = useMemo(() => {
        const mappedInvoices = financials.invoices.map(inv => ({
            type: 'Invoice', ...inv, date: inv.paid_at, document_id: inv.zoho_invoice_number,
            url: inv.zoho_invoice_url, amount: inv.total_amount,
        }));
        const mappedCreditNotes = financials.credit_notes.map(cn => ({
            type: 'Credit Note', ...cn, date: cn.created_at, document_id: cn.zoho_credit_note_number,
            url: cn.zoho_credit_note_url, amount: -cn.refund_amount,
        }));
        return [...mappedInvoices, ...mappedCreditNotes].sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [financials]);

    const handleRefundClick = (invoice) => {
        setSelectedInvoice(invoice);
        setIsRefundModalOpen(true);
    };
    
    const handleRefundSuccess = () => {
        logAction("EDIT", {
            target_entity: "INVOICE_REFUND", target_entity_id: selectedInvoice.invoice_id,
            details: `Refund initiated for invoice #${selectedInvoice.document_id}`
        });
        fetchFinancialData();
    };

    // --- NEW: Handlers for the reissue process ---
    const handleReissueClick = (invoice) => {
        setInvoiceToReissue(invoice);
        setIsReissueModalOpen(true);
    };

    const handleConfirmReissue = async () => {
        if (!invoiceToReissue) return;
        setIsReissuing(true);
        try {
            await reissueInvoiceAsGst(invoiceToReissue.invoice_id);
            toast({
                title: "Success",
                description: "Invoice reissue process has been initiated.",
                variant: "success",
            });
            fetchFinancialData(); // Refresh the list to show the updated status
        } catch (error) {
            toast({
                title: "Error",
                description: error.response?.data?.detail || "Failed to start the reissue process.",
                variant: "destructive",
            });
        } finally {
            setIsReissuing(false);
            setIsReissueModalOpen(false);
            setInvoiceToReissue(null);
        }
    };

    if (isLoading) return <p className="p-4">Loading financial data...</p>;
    if (error) return <p className="p-4 text-destructive">{error}</p>;

    return (
        <TooltipProvider>
            <div className="w-full">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Type</TableHead>
                            <TableHead>Document ID</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Product</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {combinedData.map((item) => {
                            const isReissueDisabled = item.gst_no || item.status === 'REISSUED' || item.status === 'REFUNDED';
                            return (
                            
                            <TableRow key={`${item.type}-${item.invoice_id || item.credit_note_id}`}>
                                <TableCell className="font-medium flex items-center">
                                    {item.type === 'Invoice' ? <Receipt className="h-4 w-4 mr-2" /> : <FilePlus2 className="h-4 w-4 mr-2 text-destructive" />}
                                    {item.type}
                                </TableCell>
                                <TableCell>{item.document_id || 'N/A'}</TableCell>
                                <TableCell>{format(new Date(item.date), 'dd MMM yyyy')}</TableCell>
                                <TableCell>{item.product_type || 'N/A'}</TableCell>
                                <TableCell className={item.amount < 0 ? 'text-destructive' : ''}>{formatCurrency(item.amount)}</TableCell>
                                <TableCell>{getStatusBadge(item)}</TableCell>
                                <TableCell className="flex items-center justify-end space-x-1">
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button variant="ghost" size="icon" disabled={!item.url} onClick={() => window.open(`${process.env.REACT_APP_S3_ASSETS_FETCH_URL}/${item.url}`, '_blank')}>
                                                <FileText className="h-4 w-4" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>View Document</p>
                                        </TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button variant="ghost" size="icon" disabled={!item.razorpay_payment_id && !item.razorpay_refund_id} onClick={() => window.open(item.type === 'Invoice' ? `https://dashboard.razorpay.com/app/payments/${item.razorpay_payment_id}` : `https://dashboard.razorpay.com/app/refunds/${item.razorpay_refund_id}`, '_blank')}>
                                                <ExternalLink className="h-4 w-4" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>View on Razorpay</p>
                                        </TooltipContent>
                                    </Tooltip>
                                    {item.type === 'Invoice' && hasPermission("invoices:manage") &&(
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button variant="ghost" size="icon" disabled={item.is_refunded} onClick={() => handleRefundClick(item)}>
                                                    <RefreshCw className="h-4 w-4" />
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent><p>Refund</p></TooltipContent>
                                        </Tooltip>
                                    )}

                                    {item.type === 'Invoice' && hasPermission("invoices:manage") &&(
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button variant="ghost" size="icon" disabled={isReissueDisabled} onClick={() => handleReissueClick(item)}>
                                                        <FileKey2 className="h-4 w-4" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>{isReissueDisabled ? 'Cannot be reissued' : 'Reissue as GST Invoice'}</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        )}
                                </TableCell>
                            </TableRow>
                        )})}
                    </TableBody>
                </Table>
            </div>
            {isRefundModalOpen && (
                <RefundInvoiceModal 
                    isOpen={isRefundModalOpen}
                    onClose={() => setIsRefundModalOpen(false)}
                    invoice={selectedInvoice}
                    onRefundSuccess={handleRefundSuccess}
                />
            )}

            {/* --- NEW: AlertDialog for Reissue Confirmation --- */}
            {isReissueModalOpen && (
                <AlertDialog open={isReissueModalOpen} onOpenChange={setIsReissueModalOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Reissue as GST Invoice?</AlertDialogTitle>
                            <AlertDialogDescription>
                                This will start a background process to create a credit note for the original invoice
                                and issue a new GST-compliant one.
                                <br /><br />
                                <strong>Important:</strong> Only proceed if the seller has updated their billing details with a verified GSTIN.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel disabled={isReissuing}>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={handleConfirmReissue} disabled={isReissuing}>
                                {isReissuing ? 'Initiating...' : 'Yes, Reissue Invoice'}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            )}
        </TooltipProvider>
    );
};

export default SellerFinancials;
